import { cn } from "@/lib/utils";
import { formatNet } from "./labels";

interface NetBarProps {
  /** Σ(Available − Booked): positive = free (blue, left), negative = over (red-orange, right). */
  netHours: number;
  /** Hours at which the bar is full: 300 per person, 1,200 per group. */
  scale: number;
  /** Group header rows sit on a tinted band, so the track is drawn solid there. */
  variant?: "resource" | "group";
}

/**
 * The "Net in window" bar: a fixed scale, centred on zero, so two people's bars are
 * comparable at a glance. The signed number beside it says the same thing.
 */
export function NetBar({ netHours, scale, variant = "resource" }: NetBarProps) {
  const share = Math.min(Math.abs(netHours) / scale, 1) * 50;
  const over = netHours < 0;
  return (
    <div className="flex items-center gap-2 px-2">
      <div
        aria-hidden
        className={cn(
          "relative h-2.5 w-24 shrink-0 rounded-sm",
          variant === "group" ? "border border-border bg-card" : "bg-muted",
        )}
      >
        <span
          className={cn(
            "absolute inset-y-0",
            over ? "left-1/2 bg-[var(--avail-over)]" : "right-1/2 bg-[var(--avail-free-bar)]",
          )}
          style={{ width: `${share}%` }}
        />
        <span className="absolute -inset-y-0.5 left-1/2 w-px bg-heading" />
      </div>
      <span
        className={cn(
          "font-mono text-sm",
          variant === "group" ? "font-semibold" : "font-medium",
          over ? "text-[color:var(--avail-over)]" : "text-[color:var(--avail-some-room-fg)]",
        )}
      >
        {formatNet(netHours)}
      </span>
    </div>
  );
}
